import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import SiteHeader from "../components/SiteHeader";

export default function AdminAboutUs() {
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [search, setSearch] = useState("");

  const [form, setForm] = useState({
    id: null,
    title: "",
    content: "",
    is_active: 0,
  });

  const isEditing = !!form.id;

  const loadItems = async () => {
    try {
      setLoading(true);
      setErr("");
      const { data } = await api.get("/api/aboutus");
      setItems(data?.items || []);
    } catch (e) {
      setErr(e?.response?.data?.message || "Failed to load About Us");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((x) => {
      const t = String(x.title || "").toLowerCase();
      const c = String(x.content || "").toLowerCase();
      return t.includes(q) || c.includes(q);
    });
  }, [items, search]);

  const resetForm = () => {
    setForm({ id: null, title: "", content: "", is_active: 0 });
  };

  const onEdit = (row) => {
    setForm({
      id: row.id,
      title: row.title || "",
      content: row.content || "",
      is_active: row.is_active ? 1 : 0,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this About Us item?")) return;
    try {
      setLoading(true);
      await api.delete(`/api/aboutus/${id}`);
      await loadItems();
      if (form.id === id) resetForm();
    } catch (e) {
      alert(e?.response?.data?.message || "Failed to delete");
    } finally {
      setLoading(false);
    }
  };

  const onSetActive = async (id) => {
    try {
      setLoading(true);
      await api.put(`/api/aboutus/${id}/active`);
      await loadItems();
    } catch (e) {
      alert(e?.response?.data?.message || "Failed to set active");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.title.trim()) {
      alert("Title is required");
      return;
    }
    if (!form.content.trim()) {
      alert("Content is required");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        title: form.title.trim(),
        content: form.content.trim(),
        is_active: form.is_active ? 1 : 0,
      };

      if (isEditing) {
        await api.put(`/api/aboutus/${form.id}`, payload);
      } else {
        await api.post("/api/aboutus", payload);
      }

      resetForm();
      await loadItems();
    } catch (e2) {
      alert(e2?.response?.data?.message || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SiteHeader />

      <div style={{ background: "#f3f4f6", minHeight: "100vh", padding: "18px 12px 36px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 22, fontWeight: 800, color: "#0f172a" }}>About Us</div>
              <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
                Create multiple entries and keep only one Active for the Home page.
              </div>
            </div>

            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <button
                onClick={() => navigate("/dashboard")}
                style={{
                  border: "1px solid #e2e8f0",
                  background: "#fff",
                  borderRadius: 999,
                  padding: "10px 14px",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Back
              </button>
            </div>
          </div>

          {err ? (
            <div
              style={{
                marginTop: 12,
                background: "#fef2f2",
                border: "1px solid #fecaca",
                color: "#b91c1c",
                padding: 10,
                borderRadius: 12,
                fontWeight: 700,
                fontSize: 13,
              }}
            >
              {err}
            </div>
          ) : null}

          <div style={{ display: "grid", gridTemplateColumns: "420px 1fr", gap: 14, marginTop: 14 }}>
            {/* Form */}
            <div
              style={{
                background: "#fff",
                border: "1px solid #eef2f7",
                borderRadius: 18,
                padding: 16,
                boxShadow: "0 10px 30px rgba(15,23,42,0.06)",
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 800, color: "#0f172a" }}>
                {isEditing ? "Edit About Us" : "Add About Us"}
              </div>

              <form onSubmit={handleSubmit} style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
                <div>
                  <div style={{ fontSize: 12, color: "#64748b", marginBottom: 6 }}>Title</div>
                  <input
                    value={form.title}
                    onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                    placeholder="Enter title"
                    style={{
                      width: "100%",
                      border: "1px solid #e2e8f0",
                      borderRadius: 12,
                      padding: "10px 12px",
                      fontSize: 14,
                    }}
                  />
                </div>

                <div>
                  <div style={{ fontSize: 12, color: "#64748b", marginBottom: 6 }}>Content</div>
                  <textarea
                    value={form.content}
                    onChange={(e) => setForm((p) => ({ ...p, content: e.target.value }))}
                    placeholder="Enter About Us content"
                    rows={8}
                    style={{
                      width: "100%",
                      border: "1px solid #e2e8f0",
                      borderRadius: 12,
                      padding: "10px 12px",
                      fontSize: 14,
                      resize: "vertical",
                    }}
                  />
                </div>

                <label style={{ display: "flex", gap: 10, alignItems: "center", fontSize: 13, color: "#0f172a" }}>
                  <input
                    type="checkbox"
                    checked={!!form.is_active}
                    onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked ? 1 : 0 }))}
                  />
                  Set Active (will disable others)
                </label>

                <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
                  <button
                    disabled={loading}
                    type="submit"
                    style={{
                      flex: 1,
                      border: "none",
                      background: "#2563eb",
                      color: "#fff",
                      borderRadius: 12,
                      padding: "10px 12px",
                      fontWeight: 800,
                      cursor: "pointer",
                      opacity: loading ? 0.7 : 1,
                    }}
                  >
                    {isEditing ? "Update" : "Create"}
                  </button>

                  <button
                    type="button"
                    onClick={resetForm}
                    style={{
                      border: "1px solid #e2e8f0",
                      background: "#fff",
                      borderRadius: 12,
                      padding: "10px 12px",
                      fontWeight: 800,
                      cursor: "pointer",
                    }}
                  >
                    Clear
                  </button>
                </div>
              </form>
            </div>

            {/* List */}
            <div
              style={{
                background: "#fff",
                border: "1px solid #eef2f7",
                borderRadius: 18,
                padding: 16,
                boxShadow: "0 10px 30px rgba(15,23,42,0.06)",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: "#0f172a" }}>All Items</div>
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search title/content..."
                  style={{
                    width: 260,
                    maxWidth: "100%",
                    border: "1px solid #e2e8f0",
                    borderRadius: 12,
                    padding: "9px 12px",
                    fontSize: 14,
                  }}
                />
              </div>

              {loading ? (
                <div style={{ marginTop: 12, color: "#64748b" }}>Loading…</div>
              ) : !filtered.length ? (
                <div style={{ marginTop: 12, color: "#64748b" }}>No About Us items found</div>
              ) : (
                <div style={{ marginTop: 12, overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                    <thead>
                      <tr style={{ background: "#f8fafc" }}>
                        <th style={th}>Title</th>
                        <th style={th}>Active</th>
                        <th style={{ ...th, width: 280 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((row) => (
                        <tr key={row.id} style={{ borderBottom: "1px solid #eef2f7" }}>
                          <td style={td}>
                            <div style={{ fontWeight: 800, color: "#0f172a" }}>{row.title}</div>
                            <div style={{ color: "#64748b", fontSize: 12, marginTop: 4 }}>
                              {String(row.content || "").slice(0, 120)}
                              {String(row.content || "").length > 120 ? "..." : ""}
                            </div>
                          </td>

                          <td style={td}>
                            {row.is_active ? (
                              <span style={badge("#dcfce7", "#166534")}>Active</span>
                            ) : (
                              <span style={badge("#e2e8f0", "#0f172a")}>Inactive</span>
                            )}
                          </td>

                          <td style={td}>
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                              {!row.is_active ? (
                                <button
                                  onClick={() => onSetActive(row.id)}
                                  style={btnOutline("#16a34a")}
                                >
                                  Set Active
                                </button>
                              ) : null}

                              <button onClick={() => onEdit(row)} style={btnOutline("#2563eb")}>
                                Edit
                              </button>

                              <button onClick={() => onDelete(row.id)} style={btnOutline("#ef4444")}>
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <div style={{ marginTop: 12, color: "#64748b", fontSize: 12 }}>
            Tip: Use “Set Active” to control what appears on the Home page.
          </div>
        </div>
      </div>
    </>
  );
}

const th = { textAlign: "left", padding: 10, fontSize: 12, color: "#475569", fontWeight: 800 };
const td = { padding: 10, verticalAlign: "top" };

function badge(bg, color) {
  return {
    background: bg,
    color,
    borderRadius: 999,
    padding: "6px 10px",
    fontSize: 12,
    fontWeight: 800,
    display: "inline-block",
  };
}

function btnOutline(color) {
  return {
    border: `1px solid ${color}`,
    background: "#fff",
    color,
    borderRadius: 999,
    padding: "8px 12px",
    fontWeight: 800,
    cursor: "pointer",
    fontSize: 12,
  };
}
